// NOTE: The shared product-analytics taxonomy (epic NSAI-2431, tracking plan
// NSAI-4612). Every client surface — web, mobile, browser extension — emits
// through these names and shapes so a funnel built in PostHog spans all of
// them instead of splitting per platform. The full contract, including the
// naming rules and the PII policy these types encode, lives in
// `docs/observability/posthog-tracking-plan.md`.
//
// Deliberately free of any SDK import: this package must stay isomorphic
// (see the package CLAUDE.md), so the concrete posthog-js / posthog-react-native
// clients are injected through `IProductAnalyticsClient` by each app.

// NOTE: The platform travels as a *property*, never as part of the event name,
// so `chat_message_sent` means the same thing everywhere and one funnel covers
// every surface.
export enum EAnalyticsPlatform {
  WEB = "web",
  MOBILE = "mobile",
  EXTENSION = "extension",
  SERVER = "server",
}

// NOTE: Event names follow `domain_object_verb`, past tense. Adding a value
// here is what makes an event legal — call sites must never pass a bare string,
// which is how the same concept ends up tracked under two names.
export enum EAnalyticsEvent {
  // NOTE: Activation & chat core funnel (NSAI-4615).
  AUTH_SIGNUP_COMPLETED = "auth_signup_completed",
  AUTH_LOGIN_COMPLETED = "auth_login_completed",
  AUTH_LOGOUT_COMPLETED = "auth_logout_completed",
  CHAT_CONVERSATION_CREATED = "chat_conversation_created",
  CHAT_MESSAGE_SENT = "chat_message_sent",
  CHAT_RESPONSE_RECEIVED = "chat_response_received",
  ONBOARDING_TOUR_STARTED = "onboarding_tour_started",
  // NOTE: No per-step "viewed" event. Joyride drives `setCurrentStep` from
  // several places, including a correction effect, so a naive per-step capture
  // would double-count and make the funnel wrong rather than finer. The
  // drop-off question is already answered: `step_index` rides on the
  // completed/skipped events. See NSAI-5334.
  ONBOARDING_TOUR_COMPLETED = "onboarding_tour_completed",
  ONBOARDING_TOUR_SKIPPED = "onboarding_tour_skipped",

  // NOTE: Workspace activation and the growth loop (NSAI-4616). A project is
  // the tenant boundary every event is already reported against via group
  // analytics, so its creation was the one milestone measurable only
  // indirectly. Invites are the growth loop: they are how an account stops
  // being one person's trial and becomes a team's tool.
  ORGANIZATION_CREATED = "organization_created",
  PROJECT_CREATED = "project_created",
  PROJECT_MEMBER_INVITED = "project_member_invited",

  // NOTE: Feature adoption (NSAI-4616). One event per *primary* adoption
  // signal — the action that means "this feature is being used" — rather than
  // one per CRUD verb. Deletes and renames say little about adoption and would
  // triple the surface for nothing; add one only when a question needs it.
  KNOWLEDGE_FILE_UPLOADED = "knowledge_file_uploaded",
  KNOWLEDGE_QUERIED = "knowledge_queried",
  CHAT_SHARED = "chat_shared",
  // NOTE: NSAI-5656 — a generated HTML page was opened in the live preview
  // (side panel, standalone window or the document modal). `source` says what
  // opened it: "auto" (a fresh page), "card", "version", "window", "modal".
  CHAT_HTML_PREVIEW_OPENED = "chat_html_preview_opened",
  // NOTE: NSAI-5827 — a generated page was published to a public link, or a
  // published link was taken down. Neither event carries the token or the
  // password; publishing is tracked because it moves workspace content outside
  // NSAI, which is worth seeing in aggregate.
  CHAT_PAGE_PUBLIC_LINK_CREATED = "chat_page_public_link_created",
  CHAT_PAGE_PUBLIC_LINK_REVOKED = "chat_page_public_link_revoked",
  // NOTE: NSAI-5657 / NSAI-5658 — saved dashboards. SAVED: a page was saved as
  // a dashboard (`mode`: "new" | "version", `dataMode`: "live" | "static").
  // OPENED: a dashboard was opened (`source`: "list" | "switcher" | "link").
  // REFRESHED: a refresh was started from a client (`trigger`: "open" |
  // "manual"); scheduled refreshes run on the server and are not client events.
  // SHARED: a member share or team access was granted. No event carries a
  // name, a request or page content.
  DASHBOARD_SAVED = "dashboard_saved",
  DASHBOARD_OPENED = "dashboard_opened",
  DASHBOARD_REFRESHED = "dashboard_refreshed",
  DASHBOARD_SHARED = "dashboard_shared",
  KNOWLEDGE_SHARED = "knowledge_shared",
  ASSISTANT_CREATED = "assistant_created",
  ASSISTANT_ACTIVATED = "assistant_activated",
  ASSISTANT_SHARED = "assistant_shared",
  AGENT_CREATED = "agent_created",
  AGENT_TRIGGERED = "agent_triggered",
  AGENT_HOOK_CREATED = "agent_hook_created",
  AGENT_WEBHOOK_CREATED = "agent_webhook_created",
  INTEGRATION_CONNECTED = "integration_connected",
  INTEGRATION_DISCONNECTED = "integration_disconnected",
  // NOTE: NSAI-5681 — the user tapped Connect on an inline "connect this app"
  // card under an assistant reply (the card the requestIntegrationConnection
  // tool raises). Properties: provider, action (connect | pick_connector |
  // open_integrations).
  CHAT_INTEGRATION_CONNECT_PROMPT_CLICKED = "chat_integration_connect_prompt_clicked",
  // NOTE: NSAI-6559 — a starter prompt on the empty chat was clicked (and so
  // sent). Properties: is_personalized (generated from memory vs a static
  // default), position (0-based row). Never the prompt text: a personalized row
  // paraphrases the user's memory.
  CHAT_STARTER_PROMPT_CLICKED = "chat_starter_prompt_clicked",
  CUSTOM_INTEGRATION_CREATED = "custom_integration_created",
  MODEL_CREATED = "model_created",
  MODEL_TOGGLED = "model_toggled",
  PROMPT_CREATED = "prompt_created",
  PROMPT_FOLDER_CREATED = "prompt_folder_created",
  DOC_RECOGNITION_CONFIGURED = "doc_recognition_configured",
  DOC_RECOGNITION_DOCUMENT_COMPLETED = "doc_recognition_document_completed",
  DOC_RECOGNITION_DOCUMENT_REJECTED = "doc_recognition_document_rejected",

  // NOTE: Meetings / Call Assistant (NSAI-4618).
  MEETING_UPLOAD_STARTED = "meeting_upload_started",
  MEETING_UPLOAD_COMPLETED = "meeting_upload_completed",
  // NOTE: NSAI-6451 — closes the pair above. Started/completed measures the
  // drop; this names it, carrying the uploader's own failure tag (`rejected` /
  // `network` / `stalled` / …) and the size bucket. Without it, "uploads are
  // failing" could only be turned into a cause by asking a user for a
  // screenshot. Not fired when the user cancels — that is not a failure.
  MEETING_UPLOAD_FAILED = "meeting_upload_failed",
  MEETING_JOIN_REQUESTED = "meeting_join_requested",
  MEETING_SHARED = "meeting_shared",
  MEETING_PUBLIC_LINK_CREATED = "meeting_public_link_created",
  MEETING_RETRIED = "meeting_retried",
  MEETING_FEEDBACK_SUBMITTED = "meeting_feedback_submitted",
  // NOTE: Emitted by the browser extension (NSAI-4620). Named for the product
  // domain, not the surface — `platform: "extension"` is what distinguishes
  // them, so a meeting funnel spans the extension and the web app.
  // NOTE: No `meeting_detected`. The content script notifies on every
  // transition into LOBBY *and* IN_CALL, so it fires a handful of times per
  // meeting — counting those as detections would inflate the top of the
  // extension funnel and understate the detected -> recorded conversion. It
  // needs per-tab dedup in the service worker first. See NSAI-5334.
  MEETING_RECORDING_STARTED = "meeting_recording_started",
  MEETING_RECORDING_STOPPED = "meeting_recording_stopped",

  // NOTE: UI failures (NSAI-2434). A render crash is the one "event" the user
  // never reports and we would otherwise never see.
  UI_ERROR_BOUNDARY_TRIGGERED = "ui_error_boundary_triggered",
}

// NOTE: How the session was established. Splits self-serve from enterprise SSO
// without needing to join against the billing tables.
export enum EAnalyticsAuthMethod {
  PASSWORD = "password",
  SSO = "sso",
  OAUTH = "oauth",
  INVITE = "invite",
}

// NOTE: Distinguishes a deliberate sign-out from a session the app ended on the
// user's behalf — the two mean very different things for a retention curve.
export enum EAnalyticsLogoutReason {
  USER_INITIATED = "user_initiated",
  SESSION_EXPIRED = "session_expired",
  ACCOUNT_SWITCHED = "account_switched",
}

// NOTE: Which guided tour, so onboarding effectiveness can be measured per tour
// rather than as one blended number.
export enum EAnalyticsTour {
  MAIN = "main",
  CALL_ASSISTANT = "call_assistant",
  ADMIN = "admin",
}

// NOTE: Which surface a knowledge file came in through — a folder-scoped
// upload and a global one are different user intents.
export enum EAnalyticsKnowledgeScope {
  GLOBAL = "global",
  FOLDER = "folder",
}

// NOTE: Whether a share was to named people or to a public link. The second is
// an acquisition surface, the first is collaboration — very different numbers.
export enum EAnalyticsShareKind {
  DIRECT = "direct",
  PUBLIC_LINK = "public_link",
}

// NOTE: Where a conversation was started from — tells us whether people find
// the feature through the sidebar, a deep link, an empty state or a shortcut.
export enum EAnalyticsChatSource {
  SIDEBAR = "sidebar",
  EMPTY_STATE = "empty_state",
  DEEP_LINK = "deep_link",
  SHORTCUT = "shortcut",
}

// NOTE: Bucketed magnitudes rather than raw values. The product questions
// ("are people writing one-liners or essays?", "is the p90 slow?") are all
// answerable from buckets, and a bucket cannot leak the content itself — see
// the PII rules in the tracking plan.
export enum EAnalyticsSizeBucket {
  TINY = "tiny",
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
  HUGE = "huge",
}

export enum EAnalyticsLatencyBucket {
  UNDER_1S = "under_1s",
  UNDER_3S = "under_3s",
  UNDER_10S = "under_10s",
  UNDER_30S = "under_30s",
  OVER_30S = "over_30s",
}

// NOTE: PostHog group type. Projects are the tenant boundary and the unit we
// bill, so workspace-level questions ("how many *accounts* adopted agents")
// resolve against this rather than against person counts.
export const ANALYTICS_GROUP_TYPE_PROJECT = "project";

// NOTE: Open-ended by design — each surface adds its own dimensions — but
// constrained to primitives so no call site can hand the SDK a nested object
// holding message content. See the PII rules in the tracking plan.
//
// `undefined` is deliberately absent: both SDKs type their property bags as
// JSON, which has no undefined. An absent dimension is expressed by omitting
// the key, so this rejects `{ foo: undefined }` at the call site instead of
// letting it reach the wire as a malformed property.
export type TAnalyticsPropertyValue = string | number | boolean | null;

export interface IAnalyticsProperties {
  [key: string]: TAnalyticsPropertyValue;
}

// NOTE: Person properties. Per the decision recorded in the tracking plan
// (2026-08-24) identity PII — email and name — is allowed here so support and
// sales can resolve an account; *content* PII never is, on any surface.
export interface IAnalyticsPersonProperties extends IAnalyticsProperties {
  email: string;
  name: string;
  email_domain: string;
  is_sso_user: boolean;
  provider: string;
  subscription_plan: string | null;
  created_at: string;
}

export interface IAnalyticsGroupProperties extends IAnalyticsProperties {
  name: string;
  is_personal: boolean;
}

// NOTE: The port each app implements over its own PostHog SDK. Keeping the
// taxonomy behind an interface is what lets `frontend-shared` stay free of
// browser and React Native globals while still owning the event contract.
//
// Every method is `void` and best-effort: an analytics failure must never break
// a user-facing flow, so implementations swallow their own errors and call
// sites need no try/catch.
export interface IProductAnalyticsClient {
  capture: (props: ICaptureAnalyticsEventProps) => void;
  identify: (props: IIdentifyAnalyticsUserProps) => void;
  setGroup: (props: ISetAnalyticsGroupProps) => void;
  reset: () => void;
}

export interface ICaptureAnalyticsEventProps {
  event: EAnalyticsEvent;
  properties?: IAnalyticsProperties;
}

export interface IIdentifyAnalyticsUserProps {
  distinctId: string;
  personProperties: IAnalyticsPersonProperties;
}

export interface ISetAnalyticsGroupProps {
  groupKey: string;
  groupProperties?: IAnalyticsGroupProperties;
}

// NOTE: Argument shapes for the person/group property builders. The builders
// take the already-narrowed primitives rather than `IUserProfile` so this module
// stays decoupled from the auth types and usable from the extension too.
export interface IBuildAnalyticsPersonPropertiesProps {
  email: string;
  firstName: string;
  lastName: string;
  isSsoUser: boolean;
  providers: string[];
  subscriptionPlan: string | null;
  createdAt: string;
}

// NOTE: NSAI-6105 / NSAI-6118 — argument and payload shapes for the URL
// redactor. The event envelope is structural on purpose: `before_send` has a
// different concrete type in posthog-js and posthog-react-native, and this
// package must not import either (see the file header).
export interface ISecretPathSegment {
  prefix: string;
  placeholder: string;
}

export interface IRedactAnalyticsUrlProps {
  url: string;
}

export interface IRedactAnalyticsValueProps {
  value: string;
}

export interface IAnalyticsEventEnvelope {
  properties?: Record<string, unknown>;
}

export interface IRedactAnalyticsEventProps {
  event: IAnalyticsEventEnvelope | null;
}
